<template>
   <div class="p-1">
      <!-- upload error display -->
      <div
         v-if="state.uploadError"
         data-cy="upload-error"
         class="mb-6 rounded-lg border border-alert-fg bg-alert-bg p-4"
      >
         {{ $t("system.file_upload.failed") }}
      </div>

      <!-- selected file display -->
      <div
         v-if="state.selectedFileName && !state.isUploading && !props.readOnly"
         class="flex w-full items-center justify-between rounded-md border border-grey-10 bg-grey-10 p-2"
      >
         <span class="pl-4" data-cy="selected-filename">{{ state.selectedFileName }}</span>
         <div class="flex justify-end">
            <DefaultButton data-cy="confirm-file" class="m-0 mr-2 h-12" size="sm" roundedEdges="md" @click="onUpload()">
               {{ $t("system.button_confirm") }}
            </DefaultButton>
            <DefaultButton
               data-cy="cancel-file"
               is-icon
               roundedEdges="md"
               size="sm"
               :color="Theme.Black"
               class="size-12"
               @click="removeSelectedFile()"
            >
               <span class="sr-only">{{ $t("system.file_upload.remove_file") }}</span>
               <IconTrash class="relative size-4 fill-none" />
            </DefaultButton>
         </div>
      </div>
      <!-- file input / selector -->
      <div
         v-if="!state.isUploading && !state.selectedFileName"
         class="relative flex w-full justify-center rounded-md border border-dashed bg-grey-10 py-16 text-center focus:outline-none"
         @dragover="dragover"
         @dragleave="dragleave"
         @drop="drop"
      >
         <div>
            <p>
               <label class="sr-only block">
                  {{ $t("system.file_upload.value") }}
               </label>
               <input
                  id="assetsFieldHandle"
                  ref="fileInput"
                  data-cy="file-select-input"
                  type="file"
                  :multiple="false"
                  name="fields[assetsFieldHandle][]"
                  class="absolute size-px overflow-hidden opacity-0"
                  :accept="acceptedFileTypesForInput"
                  @change="onChange"
               />
               <DefaultButton style-type="text-only" data-cy="file-select-button" @click="fileInputHandler">
                  {{ $t("system.file_upload.button_select_file") }}
               </DefaultButton>
               {{ $t("system.file_upload.or_drag_and_drop") }}
            </p>
            <p class="text-grey-70" data-cy="file-types-size-hint">
               <span class="uppercase">{{ acceptedFileTypesString }}&nbsp;</span>
               <span> {{ $t("system.file_upload.up_to") }}&nbsp;</span>
               <span class="uppercase"> 5mb</span>
               <!-- hardcode this? -->
            </p>
         </div>
      </div>

      <!-- uploading notification -->
      <div
         v-if="state.isUploading"
         data-cy="uploading-notification"
         class="mt-4 flex items-center justify-between rounded-lg border border-grey-10 bg-grey-10 px-6 py-4"
      >
         <span>{{ state.selectedFileName }}</span>
         <div class="flex items-center">
            <span class="text-grey-70">{{ $t("system.file_upload.uploading") }}</span>
            <LoadingSpinner :diameterRem="2" :borderColourClass="'text-success-fg'" class="ml-2" />
         </div>
      </div>
   </div>
</template>

<script setup lang="ts">
import DocumentsApi from "@/data/api-layer/documentsApi";
import { useI18n } from "@/vue-i18n/vue-i18n";

import { Theme } from "@/types";

interface File extends Blob {
   name: string;
}

interface Props {
   acceptedFileTypes?: Array<string>;
   readOnly?: boolean;
   blockVersionId: string;
}

const props = withDefaults(defineProps<Props>(), {
   acceptedFileTypes: () => ["image/jpeg", "image/png"],
   readOnly: false,
});

const { t: $t } = useI18n();
const state = reactive({
   uploadError: false,
   isUploading: false,
   selectedFileName: null,
   selectedFile: null as File,
   url: null,
   assetPath: null,
   fileList: [],
});

const emit = defineEmits(["file-uploaded", "file-upload-failed"]);
const fileInput = ref(null);

// 'accept' attribute takes a string containing one or more file type specifiers as its value, separated by commas.
const acceptedFileTypesForInput = computed(() => {
   return props.acceptedFileTypes.join(",");
});

// human readable list of file types
const acceptedFileTypesString = computed(() => {
   const types = [];
   if (props.acceptedFileTypes.includes("image/jpeg")) {
      types.push(".jpeg");
      types.push(".jpg");
   }
   if (props.acceptedFileTypes.includes("image/png")) {
      types.push(".png");
   }
   if (props.acceptedFileTypes.includes("application/pdf")) {
      types.push(".pdf");
   }
   const typesAsString = types.join(",");
   return typesAsString.replace(/,/g, ", ");
});

const onChange = function () {
   state.fileList = [...fileInput.value.files];
   if (state.fileList.length > 0) {
      state.selectedFile = state.fileList[0];
      state.selectedFileName = state.fileList[0].name;
      state.url = URL.createObjectURL(state.selectedFile);
   }
};
const drop = function (event) {
   if (props.readOnly) {
      return;
   }
   event.preventDefault();
   fileInput.value.files = event.dataTransfer.files;
   onChange();
};
const dragover = function (event) {
   event.preventDefault();
   if (!event.currentTarget.classList.contains("isDragging")) {
      event.currentTarget.classList.add("isDragging");
   }
};
const dragleave = function (event) {
   event.currentTarget.classList.remove("isDragging");
};
const fileInputHandler = function () {
   if (props.readOnly) {
      return;
   }
   fileInput.value.click();
};
const removeSelectedFile = function (i = 0) {
   state.assetPath = null;
   state.selectedFileName = null;
   state.selectedFile = null;
   state.fileList.splice(i, 1);
   state.url = null;
   fileInput.value = null;
};
const onUpload = async function () {
   // the upload method
   state.uploadError = false;
   state.isUploading = true;
   let presignedUrl = null;
   let s3Upload = null;

   const { name, size, type } = state.selectedFile;
   const fileInfo = {
      filename: name,
      filesize: size,
      filetype: type,
   };

   // try request for presigned url
   try {
      presignedUrl = await DocumentsApi.getUploadPresignedURL(props.blockVersionId, fileInfo);
   } catch (err) {
      state.isUploading = false;
      state.uploadError = true;
      console.error("failed to get pre-signed url", err);
      return;
   }

   // Local Document Upload
   // Replace the following try catch with s3Upload = true to test Document Upload locally
   // s3Upload = true;
   try {
      s3Upload = await DocumentsApi.uploadDocument(presignedUrl?.data?.url, state.selectedFile);
   } catch (err) {
      state.isUploading = false;
      state.uploadError = true;
      console.error("Failed to upload file:", err);
   }

   // if successful emit
   if (s3Upload) {
      setTimeout(() => {
         state.isUploading = false;
         emit("file-uploaded", name);
         removeSelectedFile();
      }, 2000);
   }
};
</script>
<style lang="scss" scoped>
.isDragging {
   @apply border-solid border-brand-dark border-2;
}
</style>
